import { AdComposedPlayer } from '@quickplay/player-interface';
import type { Logger } from '@quickplay/foundation';
import { AdFrequencyPlaybackTracker, AdFrequencyPlaybackTrackerImpl } from './AdFrequencyPlaybackTracker';
import {
  AdFrequencyControllerImpl,
  registerActiveContentConsumptionSync,
} from './AdFrequencyController';
import {
  AdFrequencyControlConfiguration,
} from './types';

/** Coordinates the tracker and controller for one logical playback session. */
export interface AFCPlaybackSession {
  /** Synchronizes pending content consumption and evaluates the next ad opportunity. */
  evaluateOpportunity(): boolean;
  /** Ends the session and releases tracker subscriptions. */
  dispose(): void;
}

/** Dependencies and AFC policy used to create a playback session. */
export interface AFCPlaybackSessionOptions {
  /** Composed player that owns the content and ad lifecycle. */
  player: AdComposedPlayer;
  /** AFC policy applied to this session. */
  adFrequencyControlConfiguration?: AdFrequencyControlConfiguration;
  /** Optional diagnostic logger. */
  logger?: Logger;
}

/** Default session composition used by provider integrations. */
export class AFCPlaybackSessionManager implements AFCPlaybackSession {
  private readonly tracker: AdFrequencyPlaybackTracker;
  private readonly control: AdFrequencyControllerImpl;
  private readonly unregisterContentConsumptionSync: () => void;
  private disposed = false;

  constructor(private readonly options: AFCPlaybackSessionOptions) {
    this.control = AdFrequencyControllerImpl.getInstance();
    this.control.configure(this.options.adFrequencyControlConfiguration ?? {});
    this.tracker = new AdFrequencyPlaybackTrackerImpl({
      player: this.options.player,
      logger: this.options.logger,
      contentConsumptionReportingIntervalInMS:
        this.options.adFrequencyControlConfiguration?.minimumContentConsumptionInMS,
    });
    this.tracker.start();
    this.unregisterContentConsumptionSync = registerActiveContentConsumptionSync(() => this.tracker.syncContentConsumption());
  }

  evaluateOpportunity(): boolean {
    if (this.disposed) {
      return true;
    }
    return !this.control ? true : this.control.evaluateOpportunity();
  }

  /** Stops the tracker and makes subsequent decisions fail open. */
  dispose(): void {
    if (this.disposed) return;
    this.unregisterContentConsumptionSync();
    this.tracker.dispose();
    this.disposed = true;
  }
}

/** Creates an AFC session for one logical playback lifecycle. */
export function createAFCPlaybackSessionManager(options: AFCPlaybackSessionOptions): AFCPlaybackSession {
  return new AFCPlaybackSessionManager(options);
}
